import { useState, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { QUIZ_STEPS, CONCEPT_TAXONOMY } from "../../data/ch_concepts";
import type { ConceptCategory } from "../../data/ch_concepts";

interface KnowledgeQuizProps {
  onSubmit: (answers: Record<string, unknown>) => void;
  isSubmitting?: boolean;
  onFetchConcepts?: (answers: Record<string, unknown>) => Promise<ConceptCategory[]>;
}

type Answers = Record<string, unknown>;

export function KnowledgeQuiz({ onSubmit, isSubmitting = false, onFetchConcepts }: KnowledgeQuizProps) {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<Answers>({});
  const [dynamicConcepts, setDynamicConcepts] = useState<ConceptCategory[] | null>(null);
  const [loadingConcepts, setLoadingConcepts] = useState(false);

  const currentStep = QUIZ_STEPS[step];
  const totalSteps = QUIZ_STEPS.length;
  const isLast = step === totalSteps - 1;

  function setAnswer(key: string, value: unknown) {
    setAnswers((prev) => ({ ...prev, [key]: value }));
  }

  function toggleCheckbox(key: string, value: string) {
    const current = (answers[key] as string[]) ?? [];
    const next = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    setAnswer(key, next);
  }

  function canProceed(): boolean {
    if (loadingConcepts) return false;
    if (!currentStep.required) return true;
    const val = answers[currentStep.id];
    if (!val) return false;
    if (typeof val === "string") return val.trim().length > 0;
    if (Array.isArray(val)) return val.length > 0;
    return true;
  }

  // Fetch adaptive concepts when entering the dynamic concepts step
  const fetchConcepts = useCallback(async () => {
    if (!onFetchConcepts) return;
    setLoadingConcepts(true);
    try {
      const result = await onFetchConcepts(answers);
      setDynamicConcepts(result);
    } catch (err) {
      console.warn("Failed to fetch adaptive concepts, using fallback", err);
      setDynamicConcepts(null); // will use static CONCEPT_TAXONOMY as fallback
    } finally {
      setLoadingConcepts(false);
    }
  }, [onFetchConcepts, answers]);

  // When step changes to concepts step, fetch adaptive concepts
  useEffect(() => {
    if (currentStep?.type === "checkbox_grid_dynamic" && dynamicConcepts === null && !loadingConcepts) {
      fetchConcepts();
    }
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleNext() {
    if (isLast) {
      onSubmit(answers);
    } else {
      setStep((s) => s + 1);
    }
  }

  function handleBack() {
    setStep((s) => s - 1);
  }

  const progressPct = ((step + 1) / totalSteps) * 100;

  // Concepts to render: dynamic (from API) or static fallback
  const conceptsToRender = dynamicConcepts ?? CONCEPT_TAXONOMY;

  return (
    <div className="flex flex-col h-full">
      {/* Progress */}
      <div className="flex-shrink-0 px-6 pt-5 pb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium text-neutral-text/50">
            Шаг {step + 1} из {totalSteps}
          </span>
          <span className="text-xs font-medium text-primary">
            {Math.round(progressPct)}%
          </span>
        </div>
        <div className="h-1.5 rounded-full bg-neutral-border overflow-hidden">
          <div
            className="h-full bg-primary rounded-full transition-all duration-500"
            style={{ width: `${progressPct}%` }}
          />
        </div>
      </div>

      {/* Step content */}
      <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-6 pb-4">
        <div className="mb-5">
          <h3 className="text-lg font-semibold text-neutral-text mb-1">
            {currentStep.title}
          </h3>
          {currentStep.subtitle && (
            <p className="text-sm text-neutral-text/60">{currentStep.subtitle}</p>
          )}
        </div>

        {/* Radio */}
        {(currentStep.type === "radio" ||
          currentStep.type === "radio_textarea" ||
          currentStep.type === "radio_motivation") && (
          <div className="space-y-2 mb-4">
            {currentStep.options?.map((opt) => {
              const selected = answers[currentStep.id] === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setAnswer(currentStep.id, opt.value)}
                  className={`w-full text-left px-4 py-3 rounded-xl border text-sm font-medium transition-all ${
                    selected
                      ? "border-primary bg-primary/5 text-primary"
                      : "border-neutral-border bg-white text-neutral-text hover:border-primary/40 hover:bg-primary/3"
                  }`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        )}

        {/* Checkbox multi (flat list) */}
        {currentStep.type === "checkbox_multi" && (
          <div className="space-y-2 mb-4">
            {currentStep.options?.map((opt) => {
              const selected = ((answers[currentStep.id] as string[]) ?? []).includes(opt.value);
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => toggleCheckbox(currentStep.id, opt.value)}
                  className={`w-full text-left px-4 py-3 rounded-xl border text-sm transition-all flex items-center gap-3 ${
                    selected
                      ? "border-primary bg-primary/5 text-primary"
                      : "border-neutral-border bg-white text-neutral-text hover:border-primary/40"
                  }`}
                >
                  <span
                    className={`flex-shrink-0 w-4 h-4 rounded border-2 flex items-center justify-center ${
                      selected ? "border-primary bg-primary" : "border-neutral-border"
                    }`}
                  >
                    {selected && (
                      <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 10 10">
                        <path d="M1.5 5L4 7.5L8.5 2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </span>
                  {opt.label}
                </button>
              );
            })}
          </div>
        )}

        {/* Static checkbox grid (concepts grouped by category) */}
        {currentStep.type === "checkbox_grid" && (
          <div className="space-y-4 mb-4">
            {CONCEPT_TAXONOMY.map((cat) => {
              const selectedConcepts = (answers["concepts"] as string[]) ?? [];
              return (
                <div key={cat.id}>
                  <p className="text-xs font-semibold text-neutral-text/50 uppercase tracking-wide mb-2">
                    {cat.label}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {cat.concepts.map((concept) => {
                      const selected = selectedConcepts.includes(concept.id);
                      return (
                        <button
                          key={concept.id}
                          type="button"
                          title={concept.brief}
                          onClick={() => toggleCheckbox("concepts", concept.id)}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                            selected
                              ? "border-primary bg-primary text-white"
                              : "border-neutral-border bg-white text-neutral-text hover:border-primary/50"
                          }`}
                        >
                          {concept.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Dynamic checkbox grid (adaptive concepts from API) */}
        {currentStep.type === "checkbox_grid_dynamic" && (
          <>
            {loadingConcepts ? (
              <div className="flex items-center justify-center py-8 gap-2 text-neutral-text/50">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span className="text-sm">Подбираю концепции под твой профиль...</span>
              </div>
            ) : (
              <div className="space-y-4 mb-4">
                {conceptsToRender.map((cat) => {
                  const selectedConcepts = (answers["concepts"] as string[]) ?? [];
                  return (
                    <div key={cat.id}>
                      <p className="text-xs font-semibold text-neutral-text/50 uppercase tracking-wide mb-2">
                        {cat.label}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {cat.concepts.map((concept) => {
                          const selected = selectedConcepts.includes(concept.id);
                          return (
                            <button
                              key={concept.id}
                              type="button"
                              title={concept.brief}
                              onClick={() => toggleCheckbox("concepts", concept.id)}
                              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                                selected
                                  ? "border-primary bg-primary text-white"
                                  : "border-neutral-border bg-white text-neutral-text hover:border-primary/50"
                              }`}
                            >
                              {concept.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* Free text */}
        {currentStep.freeTextLabel && !loadingConcepts && (
          <div>
            <label className="text-xs font-medium text-neutral-text/60 block mb-1.5">
              {currentStep.freeTextLabel}
            </label>
            <textarea
              rows={3}
              placeholder={currentStep.freeTextPlaceholder}
              value={(answers[`${currentStep.id}_text`] as string) ?? ""}
              onChange={(e) => setAnswer(`${currentStep.id}_text`, e.target.value)}
              className="w-full rounded-xl border border-neutral-border bg-white px-3 py-2 text-sm text-neutral-text placeholder:text-neutral-text/30 resize-none focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="flex-shrink-0 px-6 pb-5 pt-3 border-t border-neutral-border/40 flex items-center gap-3">
        {step > 0 && (
          <button
            type="button"
            onClick={handleBack}
            disabled={isSubmitting || loadingConcepts}
            className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl border border-neutral-border text-sm font-medium text-neutral-text hover:bg-neutral-border/20 transition-colors disabled:opacity-50"
          >
            <ChevronLeft className="w-4 h-4" />
            Назад
          </button>
        )}
        <button
          type="button"
          onClick={handleNext}
          disabled={!canProceed() || isSubmitting}
          className="ml-auto flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary text-white text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Генерирую roadmap...
            </>
          ) : isLast ? (
            "Построить Roadmap"
          ) : (
            <>
              Далее
              <ChevronRight className="w-4 h-4" />
            </>
          )}
        </button>
      </div>
    </div>
  );
}
